/**
 * OperationalSummaryTile — Phase 2a
 * ─────────────────────────────────
 * Read-only daily/weekly counts over existing AppDataContext sinks.
 * No new telemetry. No per-actor breakdown (PM identity audit blocks).
 */

import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { buildOperationalSummary } from "@/data/observability/operationalSummary";

function Stat({
  label, value, sub,
}: { label: string; value: number; sub?: string }) {
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

export function OperationalSummaryTile() {
  const {
    jobs, contractors, callbacks, holdbackLedger, disputeEvents,
    softSuggestions, overrideOutcomes,
  } = useAppData();

  const s = useMemo(
    () =>
      buildOperationalSummary({
        jobs, contractors, callbacks, holdbackLedger, disputeEvents,
        softSuggestions, overrideOutcomes,
      }),
    [jobs, contractors, callbacks, holdbackLedger, disputeEvents, softSuggestions, overrideOutcomes],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Operational Summary</h3>
          <p className="text-[11px] text-muted-foreground">
            Aggregate counts over canonical sinks • observation only
          </p>
        </div>
        <Badge variant="outline" className="text-[10px]">read-only</Badge>
      </div>

      <div>
        <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">Live state</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          <Stat label="Active jobs" value={s.activeJobs} />
          <Stat label="Probationary contractors" value={s.probationaryContractors} />
          <Stat label="Pending advisories" value={s.pendingSuggestions} />
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">Last 24 hours</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <Stat label="Callbacks filed" value={s.callbacks24h} />
          <Stat label="Disputes opened" value={s.disputesOpened24h} />
          <Stat label="Holdbacks created" value={s.holdbacksCreated24h} />
          <Stat label="Holdbacks released" value={s.holdbacksReleased24h} />
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">Last 7 days</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <Stat label="Callbacks filed" value={s.callbacks7d} />
          <Stat label="Disputes opened" value={s.disputesOpened7d} />
          <Stat label="Holdbacks created" value={s.holdbacksCreated7d} />
          <Stat label="Negative override outcomes" value={s.overridesNegative7d} />
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">Outstanding</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          <Stat label="Expired advisories" value={s.expiredSuggestions} sub="auto-expired w/o action" />
          <Stat label="Overrides pending review" value={s.overridesPending} />
        </div>
      </div>
    </div>
  );
}
